import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import Aperture from "@/components/Aperture";
import { VALUES, CITIES } from "@/lib/data";

const About = () => (
  <>
    {/* Hero */}
    <section className="relative pt-40 pb-24 md:pt-52 md:pb-32 overflow-hidden border-b border-white/5" data-testid="about-hero">
      <div className="absolute -right-32 -top-20 opacity-50 hidden md:block">
        <Aperture size={520} />
      </div>
      <div className="relative max-w-[1480px] mx-auto px-6 md:px-10">
        <Reveal>
          <div className="font-mono-tech text-[11px] tracking-[0.22em] uppercase text-[var(--gold)] mb-6">
            <span className="text-white/35 mr-3">A / 01</span>The Studio
          </div>
          <h1 className="font-display text-6xl md:text-[8vw] leading-[0.92] tracking-[-0.03em] text-white max-w-5xl">
            A studio for <span className="font-serif-display text-[var(--gold)]">light</span>, language, and
            the long view.
          </h1>
          <p className="mt-10 max-w-2xl text-white/65 text-lg leading-relaxed">
            Lucra was started by two photographers who refused to accept that wedding albums had to look like wedding albums.
            Today we run a small, deliberate studio across Atlanta and Washington, D.C. — building visual language for
            people and brands who care how they’re remembered.
          </p>
        </Reveal>
      </div>
    </section>

    {/* Manifesto */}
    <section className="relative py-24 md:py-36 border-b border-white/5">
      <div className="max-w-[1480px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
        <Reveal className="md:col-span-4">
          <div className="font-mono-tech text-[11px] tracking-[0.22em] uppercase text-[var(--gold)] mb-4">
            <span className="text-white/35 mr-3">A / 02</span>Manifesto
          </div>
          <h2 className="font-display text-3xl md:text-5xl tracking-tight text-white leading-[1.05]">
            Quiet on set.<br />Loud in the frame.
          </h2>
        </Reveal>
        <div className="md:col-span-8 space-y-6 text-white/70 leading-relaxed text-lg">
          <Reveal><p>We don’t direct so much as we listen. The best photograph is almost always the one nobody noticed us take. That’s a discipline — staying ready, staying invisible, staying close.</p></Reveal>
          <Reveal delay={0.1}><p>We treat every project like an editorial assignment, whether it’s a Saturday wedding in Buckhead or a Tuesday product shoot in a Georgetown loft. Pre-production matters. Light matters. Color science matters. And the people in front of the lens always, always matter most.</p></Reveal>
          <Reveal delay={0.2}><p>Our work is delivered fast — usually within 14 days — color-graded for OLED screens, and built for both the wall and the algorithm.</p></Reveal>
        </div>
      </div>
    </section>

    {/* Values */}
    <section className="relative py-24 md:py-36 border-b border-white/5">
      <div className="max-w-[1480px] mx-auto px-6 md:px-10">
        <div className="font-mono-tech text-[11px] tracking-[0.22em] uppercase text-[var(--gold)] mb-12">
          <span className="text-white/35 mr-3">A / 03</span>Core Values
        </div>
        <div className="grid md:grid-cols-2 gap-px bg-white/5">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={(i % 2) * 0.1}>
              <div className="bg-[#0a0a0a] p-8 md:p-12 beam-card h-full" data-testid={`value-${i}`}>
                <div className="font-mono-tech text-xs text-[var(--cyan)] mb-6">0{i + 1}</div>
                <div className="font-display text-3xl md:text-4xl text-white tracking-tight">{v.title}</div>
                <p className="mt-4 text-white/60 leading-relaxed">{v.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* Cities */}
    <section className="relative py-24 md:py-36 border-b border-white/5">
      <div className="max-w-[1480px] mx-auto px-6 md:px-10">
        <div className="font-mono-tech text-[11px] tracking-[0.22em] uppercase text-[var(--gold)] mb-12">
          <span className="text-white/35 mr-3">A / 04</span>Where we work
        </div>
        <div className="grid md:grid-cols-2 gap-8">
          {CITIES.map((c) => (
            <Reveal key={c.code}>
              <div className="border border-white/10 p-10 beam-card bg-[#0a0a0a]">
                <div className="flex items-start justify-between">
                  <div className="font-display text-7xl text-white tracking-tight">{c.code}</div>
                  <div className="text-right font-mono-tech text-[10px] tracking-[0.22em] uppercase text-[var(--cyan)]">{c.coords}</div>
                </div>
                <div className="mt-6 font-display text-2xl text-white">{c.name}, {c.state}</div>
                <p className="mt-3 text-white/60 leading-relaxed">{c.blurb}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.15}>
          <p className="mt-10 text-white/55 max-w-2xl">
            We travel. NYC, Miami, Lisbon, and the Hamptons are on our regular flight list. Destination weddings and
            international brand shoots are quoted with travel transparently itemized — no markups, no surprises.
          </p>
        </Reveal>
      </div>
    </section>

    {/* CTA */}
    <section className="relative py-24 md:py-32">
      <div className="max-w-[1480px] mx-auto px-6 md:px-10 flex flex-col md:flex-row items-start md:items-end justify-between gap-8">
        <Reveal>
          <h2 className="font-display text-4xl md:text-6xl tracking-tight text-white leading-[1.0] max-w-2xl">
            Tell us about your <span className="font-serif-display text-[var(--gold)]">project</span>.
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <Link to="/contact" className="btn-gold" data-testid="about-cta"><span>Begin</span><ArrowUpRight size={14} /></Link>
        </Reveal>
      </div>
    </section>
  </>
);

export default About;
